import { useSelector } from "react-redux";
import React, { useEffect, useState } from "react";
import { getProdutosPaginado, getTags } from "../../../api/produto";
import { getFotosPorLoja } from "../../../api/fotos";
import MetaCard from "./MetaCard";
import { FaBoxOpen, FaCamera, FaTag } from "react-icons/fa";
import ProgressoCadastro from "./ProgressoCadastro";
import { getLinksByType } from "../../../utils/storeLinks";

const metas = {
  produtos: [10, 50, 100, 200],
  fotos: [20, 100, 300, 1000],
  tags: [3, 5, 7],
};

const Metas = () => {
  const { produtos } = useSelector((state) => state.produtos);
  const { loja } = useSelector((state) => state.user);
  const { fotos } = useSelector((state) => state.fotos);
  const [tags, setTags] = useState([]);
  const [loadingTags, setLoadingTags] = useState(true);
  const [totalProdutos, setTotalProdutos] = useState(produtos.length || 0);
  const [totalFotos, setTotalFotos] = useState(fotos.length || 0);
  const [cardsExpandidos, setCardsExpandidos] = useState({
    produtos: false,
    fotos: false,
    tags: false,
    cadastroLoja: false,
  });

  const metasCadastroLoja = {
    fotoPerfil: "Adicionar uma foto de perfil",
    nomeLoja: "Definir nome da loja",
    whatsapp: "Cadastrar ao menos um WhatsApp",
    redesSociais: "Adicionar 2 redes sociais",
    geolocalizacao: "Incluir localizacao",
  };

  const redesTipos = ["instagram", "facebook", "twitter_x", "tiktok"];
  const redesCadastradas = redesTipos.reduce(
    (acc, tipo) => acc + (getLinksByType(loja, tipo).length > 0 ? 1 : 0),
    0
  );

  const progressoCadastro = {
    fotoPerfil: loja?.profile_picture ? 100 : 0,
    nomeLoja: loja?.nome_fantasia ? 100 : 0,
    whatsapp: getLinksByType(loja, "whatsapp").length > 0 ? 100 : 0,
    redesSociais: redesCadastradas >= 2 ? 100 : redesCadastradas === 1 ? 50 : 0,
    geolocalizacao:
      getLinksByType(loja, "geolocalizacao").length > 0 ||
      getLinksByType(loja, "endereco").length > 0
        ? 100
        : 0,
  };

  const metasCompletadas = Object.entries(progressoCadastro)
    .filter(([_, progresso]) => progresso === 100)
    .map(([meta]) => ({ titulo: metasCadastroLoja[meta] }));

  useEffect(() => {
    async function fetchTags() {
      try {
        const { data } = await getTags();
        setTags(data);
      } catch (error) {
        console.error("Erro ao buscar tags:", error);
      } finally {
        setLoadingTags(false);
      }
    }
    fetchTags();
  }, []);

  useEffect(() => {
    if (!loja?.id) return;

    let cancelled = false;

    async function fetchTotaisReais() {
      try {
        const [{ data: produtosResp }, { data: fotosResp }] = await Promise.all([
          getProdutosPaginado(loja.id, { page: 1, limit: 1 }),
          getFotosPorLoja(loja.id, { page: 1, limit: 1 }),
        ]);

        if (cancelled) return;

        const totalProdutosApi = Number(produtosResp?.total);
        const totalFotosApi = Number(fotosResp?.total);

        setTotalProdutos(
          Number.isFinite(totalProdutosApi) ? totalProdutosApi : produtos.length || 0
        );
        setTotalFotos(
          Number.isFinite(totalFotosApi) ? totalFotosApi : fotos.length || 0
        );
      } catch (error) {
        if (cancelled) return;
        setTotalProdutos(produtos.length || 0);
        setTotalFotos(fotos.length || 0);
      }
    }

    fetchTotaisReais();

    return () => {
      cancelled = true;
    };
  }, [loja?.id, produtos.length, fotos.length]);

  const calcularProgresso = (quantidade, metasLista) => {
    const proximaMeta =
      metasLista.find((meta) => meta > quantidade) || metasLista[metasLista.length - 1];
    return Math.min((quantidade / proximaMeta) * 100, 100);
  };

  const progresso = {
    produtos: calcularProgresso(totalProdutos, metas.produtos),
    fotos: calcularProgresso(totalFotos, metas.fotos),
    tags: calcularProgresso(tags.length, metas.tags),
  };

  const conquistarTrofeus = (quantidade, metasLista, tipo) => {
    return metasLista
      .filter((meta) => quantidade >= meta)
      .map((meta) => ({ titulo: `${meta} ${tipo} cadastrados(as)` }));
  };

  const trofeusGanhados = {
    produtos: conquistarTrofeus(totalProdutos, metas.produtos, "produtos"),
    fotos: conquistarTrofeus(totalFotos, metas.fotos, "fotos"),
    tags: conquistarTrofeus(tags.length, metas.tags, "tags"),
  };

  const alternarCard = (tipo) => {
    setCardsExpandidos((prevState) => ({
      ...prevState,
      [tipo]: !prevState[tipo],
    }));
  };

  return (
    <div>
      <h2 className="text-xl md:text-2xl font-semibold text-gray-700 mt-8 lg:mt-2 mb-4 select-none cursor-default">
        Metas
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <ProgressoCadastro
          metasCadastroLoja={metasCadastroLoja}
          progressoCadastro={progressoCadastro}
          metasCompletadas={metasCompletadas}
          alternarCard={alternarCard}
          cardsExpandidos={cardsExpandidos}
        />
        <MetaCard
          titulo="Produtos Cadastrados"
          icone={FaBoxOpen}
          quantidade={totalProdutos}
          metas={metas.produtos}
          progresso={progresso.produtos}
          trofeusGanhados={trofeusGanhados.produtos}
          cor="purple"
          tipo="produtos"
          alternarCard={alternarCard}
          cardsExpandidos={cardsExpandidos}
        />

        <MetaCard
          titulo="Fotos Cadastradas"
          icone={FaCamera}
          quantidade={totalFotos}
          metas={metas.fotos}
          progresso={progresso.fotos}
          trofeusGanhados={trofeusGanhados.fotos}
          cor="blue"
          tipo="fotos"
          alternarCard={alternarCard}
          cardsExpandidos={cardsExpandidos}
        />
        <MetaCard
          titulo="Tags Criadas"
          icone={FaTag}
          quantidade={loadingTags ? 0 : tags.length}
          metas={metas.tags}
          progresso={progresso.tags}
          trofeusGanhados={trofeusGanhados.tags}
          cor="green"
          tipo="tags"
          alternarCard={alternarCard}
          cardsExpandidos={cardsExpandidos}
        />
      </div>
    </div>
  );
};

export default Metas;
